// Broadcast event types whose toast should only be shown for games the
// recipient actually plays. SESSION_CREATED and POST_CREATED are delivered to
// every online user (BROADCAST_ALL on the backend), so without this filter a
// player would be toasted about new sessions / LFG posts in games that aren't
// in their profile.
//
// Only the toast is filtered — query invalidation still runs for every event so
// the Discover feed stays complete across all games.
export const GAME_FILTERED_TOAST_EVENT_TYPES = new Set<string>([
  'SESSION_CREATED',
  'POST_CREATED',
]);

/**
 * Returns true when a toast should be suppressed because the session's / post's
 * game is not one the recipient plays.
 *
 * Shows the toast when the event isn't game-filtered, the message carries no
 * gameId, or the recipient's games are unknown (`null` — profile not loaded
 * yet, so we don't suppress on missing data). Once the games are known, only a
 * recipient whose list includes this game is toasted — an empty list (no games
 * picked) means no game matches, so the toast is suppressed.
 */
export const shouldSuppressToastByGame = (
  type: string,
  gameId: string | null | undefined,
  userGameIds: string[] | null | undefined
): boolean => {
  if (!GAME_FILTERED_TOAST_EVENT_TYPES.has(type)) return false;
  if (!gameId) return false;
  if (userGameIds == null) return false; // games unknown (not loaded) → show
  return !userGameIds.includes(gameId);
};

/**
 * Returns true when a chat-message toast (and its coupled sound) should be
 * suppressed because the user is already looking at it, isn't looking at the
 * page at all, or has muted this conversation:
 *  - the message belongs to the chat currently open on screen (`parentId` is the
 *    chatId for chat events), or
 *  - the browser tab is hidden/backgrounded, or
 *  - the user muted this chat (`muted`, per-chat client-side setting).
 *
 * Only applies to CHAT_MESSAGE — other notification types are unaffected. The
 * unread badge + query invalidation still run regardless (handled elsewhere), so
 * the user still sees the unread count; we only skip the noisy toast/sound.
 */
export const shouldSuppressChatToast = ({
  type,
  parentId,
  activeChatId,
  tabHidden,
  muted = false,
}: {
  type: string;
  parentId: string | null | undefined;
  activeChatId: string | null | undefined;
  tabHidden: boolean;
  muted?: boolean;
}): boolean => {
  if (type !== 'CHAT_MESSAGE') return false;
  if (tabHidden) return true;
  if (muted) return true;
  return parentId != null && parentId === activeChatId;
};
